(ns app.front.view.login
  "Login page: municipality search, sign-in form and the login state machine.
   Also serves the registration and forgot-password routes, and provides the
   sign-in overlay used by the landing page."
  (:require [app.domain.branding.model :as branding-model]
            [app.front.state-machine :as sm]
            [app.front.task :as task]
            [app.front.ui :as ui]
            [app.front.ui.autofocus :as ui-af]
            [app.front.ui.locale-select :as ui-locale-select]
            [app.front.ui.password :as ui-password]
            [app.front.ui.top-bar :as top-bar]
            [app.front.util :as futil]
            [app.front.validate :as validate]
            [app.front.view.country-picker :as country-picker]
            [app.front.view.language-picker :as language-picker]
            [app.front.view.registration.confirm-email :as confirm-email]
            [app.front.view.registration.terms-of-service :as tos]
            [app.front.view.reset-password.send-link :as send-link]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.config :as front-config]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.front.redirect-notification :as redirect-notification]
            [core.util.util :as util]
            [re-chain.core :as rc]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [schema.core :as s]))

;; Form data:
(def NonBlankString (s/pred (complement str/blank?) 'string-cannot-be-blank))


;; TODO string messages must be replaced with tr translation keys
(def error-messages {:user/username [[NonBlankString "Type your email address"]]
                     :user/password [[NonBlankString "Type your password"]]})


(def initial-model {:user/username  ""
                    ::state         nil
                    ::show-errors?  false
                    ::errors        {}
                    ::server-errors nil})


(def state-machine
  {nil                   {::init ::ready}
   ::ready               {::change-username        ::username-validation
                          ::transition-next-screen ::continue}
   ::username-validation {::no-errors    ::ready
                          ::still-errors ::username-validation}
   ::continue            {::change-password   ::password-validation
                          ::try-submit        ::submitting
                          ::different-account ::ready}
   ::password-validation {::no-errors         ::continue
                          ::still-errors      ::password-validation
                          ::different-account ::ready}
   ::submitting          {::submit-user-not-exists ::continue
                          ::submit-success         ::done}})


;; -- Subscriptions -----------------------------------------------------------

(rf/reg-sub ::model
  (fn [db _] (get db ::login)))


(rf/reg-sub ::state
  :<- [::model]
  (fn [model _] (get model ::state)))


(rf/reg-sub ::username
  :<- [::model]
  (fn [model _] (get model :user/username)))


(rf/reg-sub ::password
  :<- [::model]
  (fn [model _] (get model :user/password "")))


(rf/reg-sub ::show-errors?
  :<- [::model]
  :<- [::state]
  (fn [[model state] _]
    (and (get model ::show-errors?)
         (not= state ::submitting))))


(rf/reg-sub ::server-errors
  :<- [::model]
  (fn [model _] (get model ::server-errors)))


(rf/reg-sub ::submit-disabled?
  :<- [::state]
  :<- [::model]
  (fn [[state model] _]
    (or (= state ::submitting)
        (and (#{::continue ::password-validation} state)
             (-> model :user/password str/blank?)))))


(rf/reg-sub ::password-screen?
  :<- [::state]
  (fn [state _] (contains? #{::continue ::password-validation
                             ::submitting ::done} state)))


(rf/reg-sub ::public-login-providers
  (fn [db _]
    (get db ::public-login-providers [])))

(rf/reg-sub ::sign-in-overlay-open?
  (fn [db _]
    (::sign-in-overlay-open? db false)))

(rf/reg-sub ::sign-in-redirect-path :-> ::sign-in-redirect-path)


;; -- Events ------------------------------------------------------------------

(def update-next-state (sm/make-update-next-state state-machine [::login ::state]))


(def debug (rf/after (fn [db event]
                       (.log js/console "=======")
                       (.log js/console "state: " (str (-> db ::login ::state)))
                       (.log js/console "event: " (str event)))))


(def interceptors [(when ^boolean js/goog.DEBUG debug)])


(defn handle-init
  [db [event _]]
  (-> db
      (assoc ::login initial-model)
      (update-next-state event)))


(defn handle-change-username
  [{:keys [db]} [event value]]
  {:db       (-> db
                 (assoc-in [::login :user/username] value)
                 (update ::login dissoc ::server-errors)
                 (update-next-state event))
   :dispatch [::validate-fields]})


(defn handle-change-password
  [{:keys [db]} [event value]]
  {:db       (-> db
                 (assoc-in [::login :user/password] value)
                 (update ::login dissoc ::server-errors)
                 (update-next-state event))
   :dispatch [::validate-fields]})


(defn handle-submit-clicked
  [{:keys [db]} [_ go-to-route on-success]]
  {:db       (assoc-in db [::login ::show-errors?] true)
   :dispatch (if-not (empty? (validate/validate-form (::login db) error-messages))
               [::validate-fields]
               [::try-submit go-to-route on-success])})


(defn handle-continue-clicked
  [{:keys [db]} [_ go-to-route]]
  {:db       (assoc-in db [::login ::show-errors?] true)
   :dispatch (if-not (empty? (validate/validate-form (::login db) error-messages))
               [::validate-fields]
               [::transition-next-screen go-to-route])})


(defn handle-transition-next-screen
  [{:keys [db]} [event go-to-route]]
  {:db           (assoc-in db [::login ::show-errors?] false)
   :comm/command {:command         :user/single-sign-on
                  :data            (select-keys (::login db) [:user/username])
                  :on-success      [::sso-check.success event go-to-route]
                  :on-server-error [::update-next-state event]}})


(rf/reg-event-db ::update-next-state
  (fn [db [_ original-event]]
    (-> db (update-next-state original-event))))


(rf/reg-event-fx ::sso-check.success
  (fn [{:keys [db]} [_ original-event go-to-route _ {:keys [redirect-uri]}]]
    (if redirect-uri
      (do (set! js/window.location (str redirect-uri
                                        (when (and go-to-route
                                                   (str/starts-with? go-to-route "/"))
                                          (str "?go=" (js/encodeURIComponent go-to-route)))))
          {})
      {:db (-> db (update-next-state original-event))})))


(defn handle-different-account
  [{:keys [db]} [event]]
  {:db (-> db
           (assoc-in [::login ::show-errors?] false)
           (assoc-in [::login :user/username] "")
           (update ::login dissoc :user/password)
           (update-next-state event))})


(defn handle-validate-fields
  [{:keys [db]} _]
  {:dispatch (if (empty? (validate/validate-form (::login db) error-messages))
               [::no-errors]
               [::still-errors])})


;; Events to dispatch after the user has successfully logged in
(def after-login-events [[:app.front.view.whats-coming/check-and-open]
                         [:app.front.notifications/start-backend-notifications]
                         [::task/load-my-tasks task/default-task-dashlet-id nil]
                         [:app.front.root/fetch-conversation-notification-count false]])


(rf/reg-event-db ::clear-password-field
  (fn [db]
    (assoc-in db [::login :user/password] "")))


(def frontend-version (futil/get-meta-content-by-name "vaahteraFrontendVersion"))


(rf/reg-event-fx ::try-submit
  (fn [{:keys [db]} [event go-to-route on-success]]
    {:db           (update-next-state db event)
     :comm/command {:command          :user/login
                    :data             (merge
                                        (select-keys (::login db) [:user/username :user/password])
                                        {:frontend-version frontend-version})
                    :on-bad-request   [::submit-failure.bad-request]
                    :on-server-error  [::submit-failure.server-error]
                    :on-error         [::submit-failure.server-error]
                    :on-success       [::submit-success go-to-route on-success]
                    :finally-dispatch [::clear-password-field]}}))


(rf/reg-fx ::reload-page
  (fn [_]
    (js/window.location.reload)))


(rf/reg-event-fx
  ::submit-success
  (fn [{:keys [db]} [_ original-path on-success status {:keys [user reload-needed?] :as resp}]]
    (futil/set-title nil)
    (if-let [mfa-url (-> resp :mfa :redirect-url)]
      {:navigate-to mfa-url}
      (merge {:db                     (-> db
                                          (assoc :session/user user)
                                          (assoc :notifications [])
                                          (assoc-in [::login :user/username] ""))
              :route/redirect-to-path {:path   (or original-path
                                                   (str "/" (branding-model/or-default (:branding db)) "/dashboard"))
                                       :router (:core.front.routing/router db)}}
             (if reload-needed?
               {::reload-page nil}
               {:dispatch-n (cond-> (cons [:core.front.session/restored-session status resp]
                                          after-login-events)
                              on-success (concat [on-success]))})))))


(rc/reg-chain
  ::submit-failure.bad-request
  (fn [{:keys [db]} [_ _ _]]
    ;; TODO errors can be received form server
    {:db       (assoc-in db
                         [::login ::server-errors]
                         {:user/username :error/username-or-password-wrong
                          :user/password :error/username-or-password-wrong})
     :dispatch [::submit-user-not-exists]}))


(rc/reg-chain
  ::submit-failure.server-error
  (fn [{:keys [db]} [_ _ {:keys [error]}]]
    {:db       (update-next-state db ::submit-user-not-exists)
     :dispatch [:notifications/add
                :toast
                :error
                :error/error
                (or error :error/unknown)]}))


(rf/reg-event-db ::init interceptors handle-init)

(rf/reg-event-fx ::change-username interceptors handle-change-username)
(rf/reg-event-fx ::change-password handle-change-password) ; no debug logging interceptor

(rf/reg-event-fx ::validate-fields handle-validate-fields)
(rf/reg-event-fx ::continue-clicked interceptors handle-continue-clicked)
(rf/reg-event-fx ::submit-clicked interceptors handle-submit-clicked)
(rf/reg-event-fx ::different-account interceptors handle-different-account)

(rf/reg-event-db ::no-errors update-next-state)
(rf/reg-event-db ::still-errors update-next-state)

(rf/reg-event-fx ::transition-next-screen interceptors handle-transition-next-screen)

(rf/reg-event-db ::submit-user-not-exists interceptors update-next-state)

;; Check-session:
(rf/reg-event-fx ::perform-redirect
  (fn [{{:keys [:core.front.routing/router]} :db} [_ path]]
    {:route/redirect-to-path (util/mmap path router)}))

(rf/reg-event-fx ::redirect-by-session
  (fn [{:keys [db]} [_ original-path]]
    {:async-flow {:rules [{:when     :seen?
                           :events   [:core.front.session/restored-session]
                           :dispatch [::perform-redirect (or original-path
                                                             (str "/" (branding-model/or-default (:branding db)) "/dashboard"))]
                           :halt?    true}]}}))


(rf/reg-event-fx ::fetch-public-login-providers
  (fn [_ _]
    {:comm/command {:command    :user/public-login-providers
                    :method     :get
                    :on-success [::fetch-public-login-providers-success]}}))

(rf/reg-event-db ::fetch-public-login-providers-success
  (fn [db [_ status providers]]
    (if (= status 200)
      (assoc db ::public-login-providers providers)
      db)))

(rf/reg-event-db ::open-sign-in-overlay
  (fn [db [_ redirect-path]]
    (cond-> (assoc db ::sign-in-overlay-open? true)
      redirect-path (assoc ::sign-in-redirect-path redirect-path))))

(rf/reg-event-db ::close-sign-in-overlay
  (fn [db _]
    (-> db
        (assoc ::sign-in-overlay-open? false)
        (dissoc ::sign-in-redirect-path))))


;; -- Components --------------------------------------------------------------

(defn footer-link [href text]
  [:a.text-current.no-underline.hover:underline.p-3 {:href href} text])


(defn cloudpermit-logo []
  (let [country (<sub [::front-config/country])]
    [:img.block.w-full.h-auto {:src (if (= country "ca")
                                      "/asset/img/cloudpermitCanada.svg"
                                      "/asset/img/cloudpermitUSA.svg")
                               :alt "Cloudpermit"}]))

(def placeholder-logo "/asset/img/municipalityPlaceholder.svg")

(def ^:private button-classes
  "justify-center flex-1 w-full px-4")

(defn- or-separator [tr]
  [:div.flex.items-center.my-6
   [:div.flex-1.border-solid.border-t.border-gray-300]
   [:span.px-4.text-gray-600.text-sm.font-medium (tr [:login-new/or])]
   [:div.flex-1.border-solid.border-t.border-gray-300]])

(defn- detect-provider-type
  "Detects the provider type (e.g., :google, :microsoft) based on issuer."
  [{:oidc-config/keys [issuer]}]
  (let [uri (str/lower-case (or issuer ""))]
    (cond
      (str/includes? uri "google")    :google
      (str/includes? uri "microsoft") :microsoft
      :else                           :unknown)))

(defn- provider-icon-url
  "Returns the icon URL for a known OIDC provider, or nil for unknown providers."
  [provider-type]
  (case provider-type
    :google    "/asset/img/google-icon.svg"
    :microsoft "/asset/img/microsoft-icon.svg"
    nil))

(defn- social-sign-in-button
  "Renders a single social sign-in button for an OIDC provider.
   `redirect-path` overrides the default window pathname for the post-login
   redirect (the `go=` query parameter on the authorize URL)."
  [tr provider redirect-path]
  (let [{:oidc-config/keys [id display-name]} provider
        provider-type                         (detect-provider-type provider)
        icon-url                              (provider-icon-url provider-type)
        name                                  display-name
        go-path                               (or redirect-path (.-pathname js/window.location) "")
        href-url                              (str "/api/oidc/" id "/authorize"
                                                   (when-not (str/blank? go-path)
                                                     (str "?go=" (js/encodeURIComponent go-path))))]
    [:a {:key   id
         :class "no-underline justify-center w-full px-4 py-3 rounded-md font-medium flex items-center gap-3 cursor-pointer bg-white hover:bg-gray-50 text-gray-700 border border-solid border-gray-300"
         :href  href-url}
     (when icon-url
       [:img.w-5.h-5 {:src icon-url :alt ""}])
     [:span (tr [:login-new/sign-in-with] [name])]]))

(defn- social-sign-in-buttons
  "Renders social sign-in buttons for all public login OIDC providers.
   `redirect-path` is forwarded to each button for the post-login redirect."
  [tr redirect-path]
  (let [providers (<sub [::public-login-providers])]
    (when (seq providers)
      [:div.flex.flex-col.gap-3
       (futil/for-frag [provider providers]
         [social-sign-in-button tr provider redirect-path])])))

(defn- forgot-password [tr]
  [:div.text-center.mb-2
   [:a.text-ui-night-sky-100.no-underline.hover:underline
    {:href (href/href :send-password-link-view)}
    (tr [:login/forgot])]])

(defn- continue-n-create [tr submitting?]
  [:div.flex.gap-3.mb-4
   (cc/button
     {:type       :submit
      :disabled   (boolean submitting?)
      :class-name button-classes}
     (tr [:login/next]))
   (cc/button
     {:type       :button
      :kind       :tertiary
      :class-name button-classes
      :href       (href/href :register)}
     (tr [:login-new/create-account]))])

(defn sign-in-form [tr validator go-to-route & [close-overlay]]
  (r/with-let [*password-ref (r/atom nil)
               *username-ref (r/atom nil)]
    (let [show-errors?     (<sub [::show-errors?])
          username         (<sub [::username])
          password         (<sub [::password])
          password-screen? (<sub [::password-screen?])
          submitting?      (= (<sub [::state]) ::submitting)
          submit-disabled? (<sub [::submit-disabled?])
          login-providers  (<sub [::public-login-providers])]
      [:form.flex.flex-col
       {:on-submit   (fn [e]
                       (.preventDefault e)
                       (>evt (if password-screen?
                               [::submit-clicked go-to-route close-overlay]
                               [::continue-clicked go-to-route])))
        :no-validate true}
       [:div {:class (when password-screen? "hidden")}
        [:div.mb-4
         [ui-af/autofocus {:ref           *username-ref
                           :should-focus? (not password-screen?)}
          [ui/text-input {:id            "input-email-new"
                          :light         true
                          :label-text    (tr [:login/email])
                          :type          :text
                          :value         username
                          :default-value ""
                          :auto-complete (if password-screen? "off" "username")
                          :input-class   "bg-white py-6"
                          :placeholder   (tr [:login/email-placeholder])
                          :ref           #(when (and (nil? @*username-ref) (some? %))
                                            (reset! *username-ref %))
                          :invalid       (validate/field-invalid? show-errors? validator :user/username)
                          :invalid-text  (validate/field-invalid-text show-errors? validator :user/username tr)
                          :on-change     (fn [e] (rf/dispatch-sync [::change-username (futil/tval e)]))}]]]
        [continue-n-create tr submitting?]
        [forgot-password tr]
        (when (seq login-providers)
          [:<>
           [or-separator tr]
           [social-sign-in-buttons tr go-to-route]])]
       [:div {:class (when-not password-screen? "hidden")}
        [:div.mb-4
         [ui-af/autofocus {:ref           *password-ref
                           :should-focus? password-screen?}
          [ui-password/password-input
           {:tr            tr
            :id            "input-password-new"
            :label         (tr [:login/password])
            :placeholder   (tr [:login/password-placeholder])
            :value         password
            :auto-complete "current-password"
            :class-name    "bg-white py-6"
            :handle-change #(rf/dispatch-sync [::change-password %])
            :ref           #(when (and (nil? @*password-ref) (some? %)) (reset! *password-ref %))
            :hide?         (not password-screen?)
            :invalid?      (validate/field-invalid? show-errors? validator :user/password)
            :invalid-text  (validate/field-invalid-text show-errors? validator :user/password tr)}]]]
        [:div.flex.gap-3.mb-4.m-auto
         (cc/button
           {:type       :submit
            :kind       :primary
            :disabled   (boolean submit-disabled?)
            :class-name (str button-classes " m-auto")}
           (tr [:login-new/sign-in]))]
        [forgot-password tr]
        [:div.text-center.mb-2
         [:div.mb-1.text-gray-800 (str "(" username ")")]
         [:button.bg-transparent.border-0.p-0.text-ui-night-sky-100.no-underline.hover:underline.cursor-pointer
          {:type     "button"
           :on-click #(do (.preventDefault %)
                          (>evt [::different-account]))}
          (tr [:login/different-account])]]]])))

(defn sign-in-overlay
  "A modal overlay containing the sign-in form, for use on the landing page.
   After successful login the user is redirected to the redirect path stored
   in app-db (if any), or stays on the current page."
  []
  (let [tr            (<sub [:tr])
        open?         (<sub [::sign-in-overlay-open?])
        redirect-path (<sub [::sign-in-redirect-path])
        close         [::close-sign-in-overlay]
        close!        #(>evt close)
        validator     #(or (get (<sub [::server-errors]) %)
                           (validate/validate-field % (<sub [::model]) error-messages))]
    (when open?
      (cc/composed-modal
        {:open       true
         :size       :sm
         :class-name "sign-in-overlay"
         :on-close   close!}
        (cc/modal-header {:close-modal close!
                          :title       (tr [:login-new/sign-in])})
        (cc/modal-body {}
                       [sign-in-form tr validator redirect-path close])))))

(defn header []
  [:<>
   [:div.flex.mt-2.p-4.items-center.justify-center
    (cc/link
      {:class-name "cloudpermit--logo-link w-full max-w-xs hover:no-underline"
       :href       "/"}
      [cloudpermit-logo])]
   (when-let [env (top-bar/env-name)]
     (when-not (= env "prod")
       [:div.max-w-min.bg-ui-yellow.border-ui-yellow.rounded.mx-auto
        [:span.uppercase.font-bold.flex.p-3.items-center.justify-center env]]))])

(defn login-form [match tr validator go-to-route]
  (let [register?    (-> match :data :register?)
        forgot-pass? (-> match :data :forgot-pass?)]
    [:div.bg-ui-white.rounded.rounded-tr-none.shadow-lg.w-full.border-solid.border.border-gray-300.p-4
     [:div.px-4
      [:div.mt-4
       [:h4 (tr [:login-new/welcome])]
       [:p (tr [:login-new/welcome-text])]]]
     [:hr.border-1.border-gray-100.m-0.p-0]
     [:div.px-4.mt-4
      (cond
        forgot-pass?
        [send-link/view (<sub [::username])]

        register?
        [:div
         [:a.text-ui-night-sky-100.no-underline.hover:underline.block.mb-4.flex.items-center
          {:href (href/href :login)}
          (cc/icon {:icon        i/arrow-left
                    :description ""
                    :class-name  "mr-2"})
          (tr [:login-new/back-to-sign-in])]
         [confirm-email/view false "-new"]]

        :else
        [sign-in-form tr validator go-to-route])]]))

(defn locale-pickers
  "Locale pickers. When mobile-only? is true, shown only on mobile (hidden on md+)
   and sticks to the top of the viewport. When false or not provided, hidden on
   mobile and shown on md+."
  [& [mobile-only?]]
  [:div {:class (str "w-full md:order-2"
                     (if mobile-only?
                       " md:hidden sticky top-0 z-10"
                       " hidden max-w-md md:flex"))
         :style (when mobile-only? {:background-color "#4f0891"})}
   [ui-locale-select/locale-select-wrapper {}
    [country-picker/compact-country-picker]
    (when (ff/enabled? :feature-flag.id/public-language-selector)
      [language-picker/compact-language-picker])]])


;; -- View --------------------------------------------------------------------

(defn view [match]
  (let [tr          (<sub [:tr])
        validator   #(or (get (<sub [::server-errors]) %)
                         (validate/validate-field % (<sub [::model]) error-messages))
        go-to-route (-> match :parameters :query :go)]
    [:div.flex.flex-col.w-full.min-h-screen.bg-no-repeat
     {:style {:backgroundImage    "url(/asset/img/Cloudpermit_background.svg), linear-gradient(to bottom, #efe2fc 10%, #4f0891 10%)"
              :backgroundSize     "100% auto, 100% 100%"
              :backgroundPosition "center 10%, top left"}}
     [locale-pickers true]
     [:div.w-full.max-w-md.mx-auto.p-4.md:order-1
      [header]]
     [:div.flex.flex-1.items-center.justify-center.w-full.md:order-3
      [:div.w-full.max-w-md.p-4
       {:role "main"}
       [locale-pickers]
       [login-form match tr validator go-to-route]]]
     [:div.p-4.text-white.text-center.text-sm.md:order-4
      {:role "contentinfo"}
      [footer-link "?privacy=true" (tr [:login/privacy-notice])]
      [footer-link (tr [:support-link]) (tr [:login/contact-us])]]
     [tos/doc-modal match]]))


;; -- Routes ------------------------------------------------------------------

(def route-parameters
  {:parameters {:query tos/route-parameters}})


(def route-controller
  [{:parameters {:query [:go]}
    :start      (fn [{{:keys [go]} :query}]
                  (>evt [::redirect-by-session go])
                  (>evt [::init])
                  (>evt [::fetch-public-login-providers]))}])

(def login-route
  {:name         :login
   :view         view
   :own-layout?  true
   :hide-header? true
   :hide-footer? true
   :register?    false
   :public?      true
   :controllers  (concat tos/doc-modal-controller
                         route-controller
                         ;; Only approved session-expiry and authentication notices may come from URLs.
                         [{:parameters {:query [:notification :go]}
                           :start      (fn [{{:keys [notification]} :query}]
                                         (when-let [notification (redirect-notification/parse-allowed notification)]
                                           (>evt (into [:notifications/add] notification))))}])})

(def registration-view
  {:name         :register
   :view         view
   :own-layout?  true
   :hide-header? true
   :hide-footer? true
   :register?    true
   :public?      true
   :controllers  (concat tos/doc-modal-controller
                         route-controller)})

(def send-password-link-view
  {:name         :send-password-link-view
   :view         view
   :own-layout?  true
   :hide-header? true
   :hide-footer? true
   :register?    false
   :forgot-pass? true
   :public?      true
   :controllers  (concat tos/doc-modal-controller
                         route-controller)})
